import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BarChart3, AlertTriangle, CheckCircle, Clock, TrendingUp, TrendingDown, Users, Euro, ArrowLeft, Mail, Send, Save, FileSpreadsheet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import { customerReceivedNcService } from '../services/api';

const STATUS_COLORS = {
  'Ricevuta': '#0ea5e9',
  'In valutazione': '#f59e0b',
  'In risposta': '#3b82f6',
  'In attesa di riscontro': '#8b5cf6',
  'Accettata': '#059669',
  'Parzialmente accettata': '#34d399',
  'Rifiutata': '#dc2626',
  'Chiusa': '#64748b',
};

const OUTCOME_COLORS = {
  'Accettata': '#059669',
  'Parzialmente accettata': '#f59e0b',
  'Rifiutata': '#dc2626',
  'Da valutare': '#94a3b8',
  'Non valutato': '#e2e8f0',
};

const KpiCard = ({ label, value, icon: Icon, color = 'slate', accent, testid }) => {
  const bg = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
    violet: 'bg-violet-50 border-violet-200 text-violet-800',
  }[color] || 'bg-slate-50';
  return (
    <div className={`border-2 rounded-lg p-4 ${bg}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider font-bold opacity-70">{label}</p>
        {Icon && <Icon className="w-5 h-5 opacity-60" />}
      </div>
      <p className="text-3xl font-bold mt-1">{value}</p>
      {accent && <p className="text-xs opacity-70 mt-1">{accent}</p>}
    </div>
  );
};

export default function CustomerReceivedNcDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const res = await customerReceivedNcService.dashboard(params);
      setData(res.data);
    } catch {
      toast.error('Errore caricamento dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // initial

  const applyFilter = () => load();
  const resetFilter = () => { setFromDate(''); setToDate(''); setTimeout(load, 50); };

  if (loading && !data) {
    return <div className="text-center py-16"><div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto" /></div>;
  }
  if (!data) return null;

  const { totals, by_status, by_technical_outcome, top_customers, monthly_trend } = data;

  const statusChart = by_status.filter(s => s.count > 0);
  const outcomeChart = by_technical_outcome.filter(o => o.count > 0);

  return (
    <div className="space-y-6 pb-16" data-testid="crnc-dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Link to="/customer-received-ncs">
            <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" />Elenco NC</Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-red-600" />
              Dashboard NC Cliente Ricevute
            </h1>
            <p className="text-sm text-slate-500 mt-1">Contatori per stato, esito tecnico, cliente e trend mensile</p>
          </div>
        </div>

        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Dal</Label>
            <Input type="date" className="w-40" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="dash-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Al</Label>
            <Input type="date" className="w-40" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="dash-to" />
          </div>
          <Button onClick={applyFilter} data-testid="dash-apply">Applica</Button>
          <Button variant="outline" onClick={resetFilter}>Reset</Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="Totale NC" value={totals.total} icon={AlertTriangle} color="slate" testid="kpi-total" />
        <KpiCard label="Aperte" value={totals.open} icon={Clock} color="amber" testid="kpi-open" />
        <KpiCard label="Chiuse" value={totals.closed} icon={CheckCircle} color="emerald" testid="kpi-closed" />
        <KpiCard label="Scadute" value={totals.overdue} icon={AlertTriangle} color={totals.overdue > 0 ? 'red' : 'slate'} testid="kpi-overdue" />
        <KpiCard label="Con addebito" value={totals.with_debit} icon={Euro} color="violet" testid="kpi-debit" />
        <KpiCard label="€ Richiesto" value={`€ ${(totals.total_debit_requested || 0).toFixed(2)}`} icon={Euro} color="sky" testid="kpi-debit-requested" />
        <KpiCard label="€ Accettato" value={`€ ${(totals.total_debit_accepted || 0).toFixed(2)}`} icon={Euro} color="emerald" testid="kpi-debit-accepted" />
        <KpiCard label="% Chiusura" value={totals.total > 0 ? `${Math.round((totals.closed / totals.total) * 100)}%` : '—'} icon={CheckCircle} color="emerald" testid="kpi-closure-rate" />
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By status */}
        <Card>
          <CardHeader><CardTitle className="text-base">NC per Stato</CardTitle></CardHeader>
          <CardContent>
            {statusChart.length === 0 ? (
              <p className="text-center text-slate-400 py-8">Nessun dato</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={statusChart} layout="vertical" margin={{ left: 60, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="status" tick={{ fontSize: 11 }} width={140} />
                  <Tooltip cursor={{ fill: '#f1f5f9' }} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {statusChart.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.status] || '#64748b'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* By technical outcome */}
        <Card>
          <CardHeader><CardTitle className="text-base">NC per Esito Tecnico</CardTitle></CardHeader>
          <CardContent>
            {outcomeChart.length === 0 ? (
              <p className="text-center text-slate-400 py-8">Nessun dato</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={outcomeChart}
                    dataKey="count"
                    nameKey="outcome"
                    cx="50%"
                    cy="50%"
                    outerRadius={95}
                    label={({ outcome, count }) => `${outcome}: ${count}`}
                    labelLine={false}
                  >
                    {outcomeChart.map((entry, i) => (
                      <Cell key={i} fill={OUTCOME_COLORS[entry.outcome] || '#94a3b8'} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={30} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Top customers */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Top Clienti (per numero NC)</CardTitle></CardHeader>
          <CardContent>
            {top_customers.length === 0 ? (
              <p className="text-center text-slate-400 py-8">Nessun cliente</p>
            ) : (
              <table className="w-full text-sm" data-testid="top-customers-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600">#</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Cliente</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">NC</th>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Peso</th>
                  </tr>
                </thead>
                <tbody>
                  {top_customers.map((c, i) => {
                    const pct = totals.total > 0 ? Math.round((c.count / totals.total) * 100) : 0;
                    return (
                      <tr key={c.customer_id} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs text-slate-500">{i + 1}</td>
                        <td className="px-3 py-2 font-medium">{c.customer_name}</td>
                        <td className="px-3 py-2 text-right font-bold text-red-600">{c.count}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-200 rounded-full h-2 max-w-[120px]">
                              <div className="bg-red-500 h-2 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                            <span className="text-xs text-slate-500 w-8 text-right">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Monthly trend */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Trend Mensile</CardTitle></CardHeader>
          <CardContent>
            {monthly_trend.length === 0 ? (
              <p className="text-center text-slate-400 py-8">Nessun dato per periodo selezionato</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthly_trend} margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="total" name="Totale" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="open" name="Aperte" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="closed" name="Chiuse" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Report Comparativo & Alert Settings */}
      <ComparativeReportSection />
      <AlertSettingsSection />
    </div>
  );
}

// ─────────────────────── Sotto-sezioni ───────────────────────

const DeltaBadge = ({ value, invert = false }) => {
  if (value === null || value === undefined) return <span className="text-slate-400 text-xs">—</span>;
  const positive = value > 0;
  // invert = true significa che un aumento è NEGATIVO (es. scadute, tempo di risposta)
  const good = invert ? !positive : positive;
  const color = value === 0 ? 'text-slate-500' : (good ? 'text-emerald-600' : 'text-red-600');
  const Icon = value === 0 ? null : (positive ? TrendingUp : TrendingDown);
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${color}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {value > 0 ? '+' : ''}{value}%
    </span>
  );
};

function ComparativeReportSection() {
  const [kind, setKind] = useState('quarter');
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const res = await customerReceivedNcService.comparative(k);
      setRep(res.data);
    } catch {
      toast.error('Errore caricamento report comparativo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(kind); }, [kind]);

  if (!rep) return null;

  const rows = [
    { key: 'total', label: 'Totale NC', delta: rep.current_vs_previous?.total },
    { key: 'closed', label: 'Chiuse', delta: rep.current_vs_previous?.closed },
    { key: 'overdue', label: 'Scadute (a oggi)', delta: rep.current_vs_previous?.overdue, invert: true },
    { key: 'closure_rate_pct', label: '% Chiusura', delta: rep.current_vs_previous?.closure_rate_pct, format: (v) => `${v}%` },
    { key: 'accepted', label: 'Accettate' },
    { key: 'partially_accepted', label: 'Parzialmente accettate' },
    { key: 'rejected', label: 'Rifiutate' },
    { key: 'debit_requested', label: '€ Addebito richiesto', format: (v) => `€ ${(v || 0).toFixed(2)}` },
    { key: 'debit_accepted', label: '€ Addebito accettato', delta: rep.current_vs_previous?.debit_accepted, format: (v) => `€ ${(v || 0).toFixed(2)}` },
    { key: 'avg_response_days', label: 'Tempo medio risposta (gg)', delta: rep.current_vs_previous?.avg_response_days, invert: true, format: (v) => v === null ? '—' : `${v} gg` },
  ];

  const chartData = rep.periods.map(p => ({
    label: p.label, Totale: p.total, Chiuse: p.closed, Scadute: p.overdue,
  }));

  return (
    <Card data-testid="comparative-report-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-sky-600" />
          Report Comparativo NC Cliente
        </CardTitle>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 border border-slate-200 rounded-md p-0.5">
            <button
              className={`text-xs px-3 py-1 rounded ${kind === 'quarter' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              onClick={() => setKind('quarter')}
              data-testid="report-kind-quarter"
            >Trimestrale</button>
            <button
              className={`text-xs px-3 py-1 rounded ${kind === 'year' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              onClick={() => setKind('year')}
              data-testid="report-kind-year"
            >Annuale</button>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(customerReceivedNcService.comparativeXlsxUrl(kind), '_blank')}
            data-testid="export-xlsx-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto" /></div>
        ) : (
          <div className="space-y-6">
            {/* Chart */}
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData} margin={{ left: 5, right: 20, top: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Totale" fill="#0f172a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Chiuse" fill="#059669" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Scadute" fill="#dc2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="comparative-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Metrica</th>
                    {rep.periods.map(p => (
                      <th key={p.label} className="px-3 py-2 text-right font-medium text-slate-600">
                        {p.label}
                        {p === rep.periods[rep.periods.length - 1] && <span className="ml-1 text-emerald-600 text-[10px]">(attuale)</span>}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Δ vs prec.</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{r.label}</td>
                      {rep.periods.map(p => {
                        const v = p[r.key];
                        const disp = v === null || v === undefined ? '—' : (r.format ? r.format(v) : v);
                        return <td key={p.label} className="px-3 py-2 text-right font-mono">{disp}</td>;
                      })}
                      <td className="px-3 py-2 text-right"><DeltaBadge value={r.delta} invert={r.invert} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AlertSettingsSection() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const res = await customerReceivedNcService.overdueAlertSettings.get();
      setSettings(res.data);
    } catch { /* silenzio */ }
  };
  useEffect(() => { load(); }, []);

  if (!settings) return null;

  const save = async () => {
    setSaving(true);
    try {
      await customerReceivedNcService.overdueAlertSettings.update({
        enabled: settings.enabled,
        recipients: (settings.recipients_text || '').split(',').map(s => s.trim()).filter(s => s.includes('@')),
        schedule_hour: parseInt(settings.schedule_hour, 10) || 7,
        schedule_minute: parseInt(settings.schedule_minute, 10) || 30,
      });
      toast.success('Impostazioni salvate');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await customerReceivedNcService.overdueAlertSettings.sendNow();
      if (res.data.status === 'sent') {
        toast.success(`Alert inviato a ${res.data.sent_to?.length || 0} destinatari (${res.data.overdue_count} NC scadute)`);
      } else {
        toast.info(res.data.reason || res.data.status);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore invio test');
    } finally {
      setTesting(false);
    }
  };

  const recipientsText = settings.recipients_text ?? (settings.recipients || []).join(', ');

  return (
    <Card data-testid="alert-settings-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Mail className="w-4 h-4 text-red-600" />
          Alert Scadenze via Email
        </CardTitle>
        <div className="flex items-center gap-2">
          <Label htmlFor="alert-enabled" className="text-sm cursor-pointer">Attivo</Label>
          <Switch
            id="alert-enabled"
            checked={!!settings.enabled}
            onCheckedChange={(v) => setSettings(s => ({ ...s, enabled: v }))}
            data-testid="alert-enabled-switch"
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">
          Ogni giorno il sistema controlla le NC cliente con scadenza risposta superata e non ancora chiuse.
          Se ne trova, invia un&apos;email di riepilogo ai destinatari indicati.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1 md:col-span-2">
            <Label>Destinatari (separati da virgola)</Label>
            <Input
              value={recipientsText}
              onChange={(e) => setSettings(s => ({ ...s, recipients_text: e.target.value }))}
              placeholder="qa@evotech.it, quality-manager@evotech.it"
              data-testid="alert-recipients-input"
            />
            <p className="text-xs text-slate-500">
              Vuoto: usa l&apos;email nel setting firma QA (se presente), altrimenti gli utenti con ruolo qualità/admin.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label>Ora</Label>
              <Input
                type="number" min="0" max="23"
                value={settings.schedule_hour ?? 7}
                onChange={(e) => setSettings(s => ({ ...s, schedule_hour: e.target.value }))}
                data-testid="alert-hour-input"
              />
            </div>
            <div className="space-y-1">
              <Label>Minuti</Label>
              <Input
                type="number" min="0" max="59"
                value={settings.schedule_minute ?? 30}
                onChange={(e) => setSettings(s => ({ ...s, schedule_minute: e.target.value }))}
                data-testid="alert-minute-input"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <p className="text-xs text-slate-500">
            Ultimo aggiornamento: {settings.updated_at ? new Date(settings.updated_at).toLocaleString('it-IT') : 'mai'}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={sendTest} disabled={testing || !settings.enabled} data-testid="alert-send-test-btn">
              <Send className="w-4 h-4 mr-1" />
              {testing ? 'Invio...' : 'Invia ora (test)'}
            </Button>
            <Button onClick={save} disabled={saving} data-testid="alert-save-btn">
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Salvo...' : 'Salva impostazioni'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
